import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  IActionType,
  VariableResultCustomText,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { BuilderComponentGroup, BuilderComponentSettingType, ComponentSettingKey, SpacingInputUnit } from '../types'
import { LABELS } from '../types/labels'
import {
  hoverEffectOptions,
  makeDataSource,
  makeFilter,
  makeSorting,
  makeSpacing,
  makeTextColorSettings,
  makeTypographyGroup,
} from '../utils/components'

export const InteractiveComponents: BuilderComponentGroup = {
  id: 'interactive',
  title: LABELS.INTERACTIVE_KEY,
  components: [
    {
      compId: 'comment',
      title: LABELS.COMMENTS_KEY,
      searchKeywords: ['comments', 'discussion', 'chat', 'thread', 'forum', 'feedback', 'replies'],
      icon: 'Comment24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.CommentDataSource),
              {
                id: ComponentSettingKey.CommentDataComment,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COMMENT_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.RichText, ColumnTypes.Text],
                },
              },
              {
                id: ComponentSettingKey.CommentDataAuthor,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.AUTHOR_NAME_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CommentDataAvatar,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.AUTHOR_AVATAR_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Image, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CommentDataParent,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.PARENT_COMMENT_KEY,
                help: 'Select a column that points to the parent id of the comment. If unspecified, nested comment will not be supported. ',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.RowID, ColumnTypes.SessionID, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CommentDataUser,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.USER_COMMENT_KEY,
                help: 'Select a column that points to user id or email of the comment. If unspecified, the user click action will not be supported. ',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.RowID, ColumnTypes.SessionID, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CommentDataDate,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.TIMESTAMP_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.DateTime],
                },
              },
              {
                id: ComponentSettingKey.CommentDataLikes,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LIKES_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              {
                id: ComponentSettingKey.CommentDataDislikes,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.DISLIKES_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CommentDataSource,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              // {
              //   id: ComponentSettingKey.CommentDataApproved,
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   label: LABELS.APPROVED_KEY,
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.CommentDataSource,
              //     columnTypes: [ColumnTypes.Boolean],
              //   },
              // },
            ],
          },
          [LABELS.MENU_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDataActionMenu,
                label: LABELS.ACTION_MENU_KEY,
                type: BuilderComponentSettingType.MenuSelect,
                menuSelectSettings: {
                  autoDefault: true,
                },
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.CommentDataSortBy,
            ComponentSettingKey.CommentDataSortType,
            ComponentSettingKey.CommentDataSource,
            'desc'
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.CommentDataFilter,
            ComponentSettingKey.CommentDataSource
          ),
        },
        designGroup: {
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDataLimit,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.COMPONENT_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.COUNTER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignShowLikeCount,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.SHOW_LIKE_COUNT_KEY,
              },
              {
                id: ComponentSettingKey.CommentDesignShowDislikeCount,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.SHOW_DISLIKE_COUNT_KEY,
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
              },
              {
                id: ComponentSettingKey.CommentDesignItemGap,
                type: BuilderComponentSettingType.SpacingSingle,
                label: 'Item Gap',
                defaultValue: {
                  value: 8,
                  unit: SpacingInputUnit.Pixel,
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.CommentDesignTypography, [
            {
              id: ComponentSettingKey.CommentDesignAuthorTypography,
              label: LABELS.AUTHOR_NAME_KEY,
              defaultValue: 'Label Large',
            },
            {
              id: ComponentSettingKey.CommentDesignBodyTypography,
              label: LABELS.COMMENT_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.CommentDesignReplyButtonTypography,
              label: LABELS.BUTTON_KEY,
              defaultValue: 'Label Medium',
            },
            {
              id: ComponentSettingKey.CommentDesignDateTypography,
              label: LABELS.DATE_KEY,
              defaultValue: 'Body Medium',
            },
          ]),
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignGroupTextColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.COMMENT_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignCommentColor,
                      ComponentSettingKey.CommentDesignCommentColorSolid,
                      ComponentSettingKey.CommentDesignCommentColorStartGradient,
                      ComponentSettingKey.CommentDesignCommentColorEndGradient,
                      ComponentSettingKey.CommentDesignCommentColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.COMMENT_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.AUTHOR_NAME_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignAuthorNameColor,
                      ComponentSettingKey.CommentDesignAuthorNameColorSolid,
                      ComponentSettingKey.CommentDesignAuthorNameColorStartGradient,
                      ComponentSettingKey.CommentDesignAuthorNameColorEndGradient,
                      ComponentSettingKey.CommentDesignAuthorNameColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.AUTHOR_NAME_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.AUTHOR_AVATAR_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignAuthorAvatarColor,
                      ComponentSettingKey.CommentDesignAuthorAvatarColorSolid,
                      ComponentSettingKey.CommentDesignAuthorAvatarColorStartGradient,
                      ComponentSettingKey.CommentDesignAuthorAvatarColorEndGradient,
                      ComponentSettingKey.CommentDesignAuthorAvatarColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.AUTHOR_AVATAR_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.TIME_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignTimeColor,
                      ComponentSettingKey.CommentDesignTimeColorSolid,
                      ComponentSettingKey.CommentDesignTimeColorStartGradient,
                      ComponentSettingKey.CommentDesignTimeColorEndGradient,
                      ComponentSettingKey.CommentDesignTimeColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.TIME_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LIKE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignLikeColor,
                      ComponentSettingKey.CommentDesignLikeColorSolid,
                      ComponentSettingKey.CommentDesignLikeColorStartGradient,
                      ComponentSettingKey.CommentDesignLikeColorEndGradient,
                      ComponentSettingKey.CommentDesignLikeColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.LIKE_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.DISLIKE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.CommentDesignDislikeColor,
                      ComponentSettingKey.CommentDesignDislikeColorSolid,
                      ComponentSettingKey.CommentDesignDislikeColorStartGradient,
                      ComponentSettingKey.CommentDesignDislikeColorEndGradient,
                      ComponentSettingKey.CommentDesignDislikeColorGradientDirection,
                      ComponentSettingKey.CommentDesignScheme,
                      LABELS.DISLIKE_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CommentDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.CommentActionCommentSubmit,
            label: 'On Comment Submit',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.AddNewRow,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.CommentActionReplySubmit,
            label: 'On Reply Submit',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.AddNewRow,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.CommentActionAuthorClick,
            label: 'On Author Click',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.ShowDetailsScreen,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.CommentActionLikeClick,
            label: 'On Like Click',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.CommentActionDislikeClick,
            label: 'On Dislike Click',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
        ],
        behavior: {
          custom: {
            Comment: {
              items: [
                {
                  id: ComponentSettingKey.CommentBehaviorAutoApprove,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.AUTO_APPROVE_KEY,
                },
                {
                  id: ComponentSettingKey.CommentBehaviorShowLikeCount,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.SHOW_LIKE_COUNT_KEY,
                },
                {
                  id: ComponentSettingKey.CommentBehaviorShowDislikeCount,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.SHOW_DISLIKE_COUNT_KEY,
                },
              ],
            },
          },
          visibility: true,
        },
      },
    },
    // {
    //   compId: 'like',
    //   title: 'Like',
    //   icon: 'Thumbs24',
    //   nested: false,
    //   //detailDataSource: undefined,
    //   settings: {
    //     data: [
    //       {
    //         id: ComponentSettingKey.LikeDataValue,
    //         type: BuilderComponentSettingType.VariableSelect,
    //         label: 'Value',
    //         variableSelectSettings: {
    //           flags:
    //             VariableSelectFeatureFlag.ColumnData |
    //             VariableSelectFeatureFlag.CurrentRow |
    //             VariableSelectFeatureFlag.SingleCell,
    //           columnTypes: [ColumnTypes.Number],
    //         },
    //       },
    //       {
    //         id: ComponentSettingKey.LikeDataIcon,
    //         type: BuilderComponentSettingType.VariableSelect,
    //         label: 'Icon',
    //         variableSelectSettings: {
    //           flags:
    //             VariableSelectFeatureFlag.ColumnData |
    //             VariableSelectFeatureFlag.CurrentRow |
    //             VariableSelectFeatureFlag.SingleCell |
    //             VariableSelectFeatureFlag.Icon,
    //           columnTypes: [ColumnTypes.Icon],
    //         },
    //       },
    //     ],
    //     designGroup: {
    //       'Color Scheme': makeSchemeItems(ComponentSettingKey.LikeDesignScheme),
    //       Typography: makeTypography(ComponentSettingKey.LikeDesignTypography, 'Label Large'),
    //     },
    //     behavior: {
    //       visibility: true,
    //     },
    //   },
    // },
    {
      compId: 'rating',
      title: LABELS.RATING_KEY,
      searchKeywords: ['rating', 'stars', 'feedback', 'reviews', 'vote', 'score', 'star rating'],
      icon: 'MiStarHalf24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_INPUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.RatingDataValue,
                type: BuilderComponentSettingType.VariableSelect,
                label: LABELS.VALUE_KEY,
                defaultValue: {
                  flag: VariableSelectFeatureFlag.CustomText,
                  value: '3.5',
                } as VariableResultCustomText,
                variableSelectSettings: {
                  flags:
                    VariableSelectFeatureFlag.ColumnData |
                    VariableSelectFeatureFlag.CurrentRow |
                    VariableSelectFeatureFlag.SingleCell |
                    VariableSelectFeatureFlag.CustomText,
                  columnTypes: [ColumnTypes.Rating, ColumnTypes.Number],
                },
              },
              {
                id: ComponentSettingKey.RatingDataCount,
                type: BuilderComponentSettingType.Slider,
                label: 'Count',
                defaultValue: [5],
                sliderSettings: {
                  max: 10,
                },
              },
              {
                id: ComponentSettingKey.RatingDataInterval,
                type: BuilderComponentSettingType.Slider,
                label: 'Interval',
                defaultValue: [1],
                sliderSettings: {
                  max: 1,
                  min: 0.5,
                  step: 0.5,
                },
              },
            ],
          },
          Text: {
            items: [
              {
                id: ComponentSettingKey.RatingDataText,
                type: BuilderComponentSettingType.VariableText,
                label: 'Text',
                variableTextSettings: {
                  flags: VariableTextFeatureFlag.SingleCell,
                  columnTypes: [ColumnTypes.Text],
                },
              },
            ],
          },
          'Rating Distribution': {
            items: [
              {
                id: ComponentSettingKey.RatingData1Start,
                type: BuilderComponentSettingType.Number,
                label: '1 Star Count',
                defaultValue: 0,
                numberSettings: {
                  max: 10,
                  min: 0,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.RatingData2Start,
                type: BuilderComponentSettingType.Number,
                label: '2 Star Count',
                defaultValue: 0,
                numberSettings: {
                  max: 100,
                  min: 0,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.RatingData3Start,
                type: BuilderComponentSettingType.Number,
                label: '3 Star Count',
                defaultValue: 0,
                numberSettings: {
                  max: 100,
                  min: 0,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.RatingData4Start,
                type: BuilderComponentSettingType.Number,
                label: '4 Star Count',
                defaultValue: 0,
                numberSettings: {
                  max: 100,
                  min: 0,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.RatingData5Start,
                type: BuilderComponentSettingType.Number,
                label: '5 Star Count',
                defaultValue: 0,
                numberSettings: {
                  max: 100,
                  min: 0,
                  step: 1,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.RatingDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
                defaultValue: 'parent',
              },
            ],
          },
          [LABELS.SIZE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.RatingDesignWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.WIDTH_KEY,
                defaultValue: [100],
                sliderSettings: {
                  max: 100,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
              {
                id: ComponentSettingKey.RatingDesignSize,
                type: BuilderComponentSettingType.Select,
                label: LABELS.SIZE_KEY,
                defaultValue: 'md',
                selectSettings: {
                  options: [
                    {
                      label: 'Small',
                      value: 'sm',
                    },
                    {
                      label: 'Medium',
                      value: 'md',
                    },
                    {
                      label: 'Large',
                      value: 'lg',
                    },
                    {
                      label: 'Extra Large',
                      value: 'xl',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: makeSpacing({
            margin: {
              id: ComponentSettingKey.RatingDesignMargin,
              defaultValue: { x: 10, y: 0 },
            },
          }),
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ButtonDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.PROGRESS_BAR_KEY]: [
                    {
                      id: ComponentSettingKey.RatingDesignProgressColor,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Progress Color',
                      colorVariantSettings: {
                        schemeId: ComponentSettingKey.RatingDesignScheme,
                        fullColor: 'fg',
                      },
                      dependOn: {
                        id: ComponentSettingKey.RatingDesignVariant,
                        showWhen: (v: unknown) => v === 'advanced',
                      },
                    },
                  ],
                  [LABELS.RATING_KEY]: [
                    // ...makeTextColorSettings(
                    //   ComponentSettingKey.RatingDesignProgressColor,
                    //   ComponentSettingKey.RatingDesignProgressColorSolid,
                    //   undefined,
                    //   undefined,
                    //   undefined,
                    //   ComponentSettingKey.RatingDesignScheme,
                    //   'Progress Color',
                    //   'adaptive',
                    //   { showGradient: false }
                    // ).items,
                    ...makeTextColorSettings(
                      ComponentSettingKey.RatingDesignColor,
                      ComponentSettingKey.RatingDesignColorSolid,
                      undefined,
                      undefined,
                      undefined,
                      ComponentSettingKey.RatingDesignScheme,
                      'Comment',
                      'adaptive',
                      { showGradient: false }
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.VARIANTS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.RatingDesignVariant,
                type: BuilderComponentSettingType.Select,
                label: LABELS.VARIANTS_KEY,
                defaultValue: 'primary',
                selectSettings: {
                  options: [
                    {
                      label: 'Default',
                      value: 'default',
                    },
                    {
                      label: 'With Text',
                      value: 'with-text',
                    },
                    {
                      label: 'With Count',
                      value: 'count',
                    },
                    {
                      label: 'Advanced',
                      value: 'advanced',
                    },
                  ],
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },
    // {
    //   compId: 'stopwatch',
    //   title: LABELS.STOPWATCH_KEY,
    //   icon: 'Timer24',
    //   nested: false,
    //   //detailDataSource: undefined,
    //   settings: {
    //     data: [
    //       makeDataSource(ComponentSettingKey.StopwatchDataSource),
    //       {
    //         id: ComponentSettingKey.StopwatchDataDuration,
    //         type: BuilderComponentSettingType.ColumnSelect,
    //         help: LABELS.ACCEPT_DATETIME_HELP_KEY,
    //         label: LABELS.DURATION_KEY,
    //         columnSelectSettings: {
    //           tableSourceId: ComponentSettingKey.StopwatchDataSource,
    //           columnTypes: [ColumnTypes.DateTime],
    //         },
    //       },
    //     ],
    //     design: [makeScheme(ComponentSettingKey.StopwatchDesignScheme)],
    //     behavior: {
    //       custom: {
    //         [LABELS.GENERAL_KEY]: {
    //           items: [
    //             {
    //               id: ComponentSettingKey.StopwatchBehaviorStartCountdown,
    //               type: BuilderComponentSettingType.Number,
    //               label: LABELS.COUNTDOWN_TO_START_KEY,
    //               numberSettings: {
    //                 max: 60,
    //                 min: 0,
    //                 step: 1,
    //               },
    //             },
    //             {
    //               id: ComponentSettingKey.StopwatchBehaviorCountdownSound,
    //               type: BuilderComponentSettingType.Select,
    //               label: LABELS.COUNTDOWN_SOUND_KEY,
    //               forceHorizontal: true,
    //               selectSettings: {
    //                 options: [
    //                   {
    //                     label: LABELS.BELL_KEY,
    //                     value: 'bell',
    //                     icon: 'Tune16',
    //                   },
    //                   {
    //                     label: LABELS.PING_KEY,
    //                     value: 'ping',
    //                     icon: 'Tune16',
    //                   },
    //                 ],
    //               },
    //             },
    //             {
    //               id: ComponentSettingKey.StopwatchBehaviorResetWarning,
    //               type: BuilderComponentSettingType.Switch,
    //               label: LABELS.SHOW_WARNING_ON_RESET_KEY,
    //             },
    //           ],
    //         },
    //       },
    //       visibility: true,
    //     },
    //   },
    // },
  ],
}
